import React, { useMemo, useState } from 'react';
import { withAuthenticationRequired } from '../providers/withAuthenticationRequired';
import { Badge, Button, Card, Col, Container, Row, Spinner, Table } from 'react-bootstrap';
import { FaEnvelope } from 'react-icons/fa';
import MonitoringListSelect from '../Components/FormComponents/MonitoringListSelect';
import RegistrationRequestModal from '../Components/Communications/RegistrationRequestModal';
import { useClientsMissingRegistrationsQuery } from '../queries/communication.query';

const formatDate = (value: string | null) => {
  if (!value) return 'Never';
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

function CommunicationsPage() {
  const [monitoringListId, setMonitoringListId] = useState('');
  const [showModal, setShowModal] = useState(false);

  const {
    data: clients,
    isLoading,
    refetch
  } = useClientsMissingRegistrationsQuery(monitoringListId || undefined);

  const summary = useMemo(() => {
    const list = clients ?? [];
    return {
      clientCount: list.length,
      plateCount: list.reduce((total, client) => total + client.missingPlateCount, 0),
      unreachable: list.filter((client) => !client.emailAddress && !client.phoneNumber).length
    };
  }, [clients]);

  return (
    <Container fluid className="py-4">
      <h3>Communications</h3>
      <p className="text-muted">
        Customers with at least one monitored plate (one with a monitoring range) that has no
        registration document on file.
      </p>

      <Row className="g-3 mb-4">
        <Col md={3}>
          <Card body>
            <div className="text-muted small">Clients missing registrations</div>
            <h4 className="mb-0">{summary.clientCount}</h4>
          </Card>
        </Col>
        <Col md={3}>
          <Card body>
            <div className="text-muted small">Monitored plates without a registration</div>
            <h4 className="mb-0">{summary.plateCount}</h4>
          </Card>
        </Col>
        <Col md={3}>
          <Card body>
            <div className="text-muted small">No email or phone on file</div>
            <h4 className="mb-0">{summary.unreachable}</h4>
          </Card>
        </Col>
        <Col md={3} className="d-flex align-items-end">
          <Button
            variant="primary"
            className="w-100 d-flex align-items-center justify-content-center gap-2"
            disabled={isLoading || summary.clientCount === 0}
            onClick={() => setShowModal(true)}>
            <FaEnvelope /> Request Registrations
          </Button>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={4}>
          <label className="fw-bold small mb-1">Monitoring List</label>
          <MonitoringListSelect
            handleDataChange={(_key, value) => setMonitoringListId(value)}
            value={monitoringListId}
            addNew={false}
            showAllOption
          />
        </Col>
      </Row>

      {isLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : summary.clientCount === 0 ? (
        <h5 className="text-center text-muted py-5">
          Every monitored plate has a registration on file.
        </h5>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Client</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Missing</th>
              <th>Plates</th>
              <th>Last Requested</th>
            </tr>
          </thead>
          <tbody>
            {(clients ?? []).map((client) => (
              <tr key={client.clientId}>
                <td>{client.clientName}</td>
                <td className="small">
                  {client.emailAddress ?? (
                    <span className="text-danger fst-italic">None on file</span>
                  )}
                </td>
                <td className="small text-nowrap">
                  {client.phoneNumber ?? (
                    <span className="text-danger fst-italic">None on file</span>
                  )}
                </td>
                <td>
                  {client.missingPlateCount} of {client.monitoredPlateCount} monitored
                </td>
                <td>
                  {client.missingPlateNumbers.map((plate) => (
                    <Badge bg="secondary" className="me-1" key={plate}>
                      {plate}
                    </Badge>
                  ))}
                </td>
                <td className="small">{formatDate(client.lastRequestedAt)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <RegistrationRequestModal
        show={showModal}
        onHide={() => setShowModal(false)}
        clients={clients ?? []}
        onSent={() => refetch()}
      />
    </Container>
  );
}

export default withAuthenticationRequired(CommunicationsPage);
